import { Spacing } from "@tomis-tech/types";

import { api } from "@/api";
import { getChatApi } from "@/chatApi";
import { isObject, storage } from "@/utils";
import {
  ChatWidgetConfiguration,
  ChatWidgetHandoffStatus,
  ChatWidgetLoadResponse,
  ChatWidgetUserIdentity,
} from "@/types";

interface InitializeResult {
  conversationHistory?: ChatWidgetLoadResponse["conversation_history"];
  forms?: ChatWidgetLoadResponse["forms"];
  requiredForm?: ChatWidgetLoadResponse["required_form"];
  handoffStatus?: ChatWidgetHandoffStatus;
  config?: ChatWidgetConfiguration;
  success: boolean;
  errorMessage?: string;
  /** Missing on an old backend, which the tracker treats as enabled. */
  trackingEnabled?: boolean;
}

let sharedInitialize: {
  siteId: string;
  promise: Promise<InitializeResult>;
} | null = null;

/** Fetch TOMIS API to get ChatBot configuration.
 *
 * Then sanitize and merge configuration from API with
 * settings from `load` function & default settings.
 *
 * Also updates chat API variables when appropriate.
 *
 * Parallel `load()`s for the same site share one `/chat-widget/load/` request.
 */
export async function initialize(
  id: string,
  settings: Partial<ChatWidgetConfiguration>,
  user?: ChatWidgetUserIdentity,
  requestedFormKey?: string,
  preview?: boolean,
): Promise<InitializeResult> {
  if (sharedInitialize && sharedInitialize.siteId === id) {
    return sharedInitialize.promise;
  }

  const promise = initializeFromNetwork(
    id,
    settings,
    user,
    requestedFormKey,
    preview,
  ).then(
    (result) => {
      if (!result.success && sharedInitialize?.promise === promise) {
        sharedInitialize = null;
      }
      return result;
    },
    (error: unknown) => {
      if (sharedInitialize?.promise === promise) {
        sharedInitialize = null;
      }
      throw error;
    },
  );

  sharedInitialize = { siteId: id, promise };
  return promise;
}

async function initializeFromNetwork(
  id: string,
  settings: Partial<ChatWidgetConfiguration>,
  user?: ChatWidgetUserIdentity,
  requestedFormKey?: string,
  preview?: boolean,
): Promise<InitializeResult> {
  getChatApi().chat.id = id;

  const existingUserId = storage.getUserId(id);
  const existingConversationId = storage.getConversationId(id);

  const body: Record<string, string | number> = {
    referrer_url: window.document.referrer,
    page_url: window.location.href,
  };

  if (existingUserId) {
    body.user_id = existingUserId;
  }

  if (existingConversationId) {
    body.conversation_id = existingConversationId;
  }

  // Pass requested form key to backend if provided
  // Backend will validate and return required_form if valid
  if (requestedFormKey && typeof requestedFormKey === "string") {
    body.requested_form_key = requestedFormKey;
  }

  if (preview) {
    body.preview = 1;
  }

  if (user) {
    if (typeof user.name === "string" && user.name) {
      body.name = user.name;
    }
    if (typeof user.email === "string" && user.email) {
      body.email = user.email;
    }
    if (typeof user.phone === "string" && user.phone) {
      body.phone = user.phone;
    }
  }

  const { data, error } = await api.post<ChatWidgetLoadResponse>(
    `/${id}/chat-widget/load/`,
    body,
  );

  if (error || !data) {
    return {
      success: false,
      errorMessage: error || "Failed to load chat widget configuration",
    };
  }

  const {
    config,
    user_id,
    conversation_history,
    conversation_id,
    handoff_status,
    forms,
    required_form,
    tracking_enabled,
  } = data;

  // Always update user ID based on API result
  // This is necessary because localStorage could be corrupted
  // If an invalid user id is provided to backend, it will be ignored and new user created
  storage.setUserId(user_id, id);

  if (conversation_id) {
    storage.setConversationId(conversation_id, id);
  } else if (existingConversationId) {
    // Stale or invalid conversation id was provided to API, so clear storage
    storage.setConversationId("", id);
  }

  const pageSettings = sanitizeSettings(settings);
  const databaseSettings = sanitizeSettings(config || {});

  /** Merge configuration from endpoint
   * with configuration provided to `load` function */
  const initConfig: ChatWidgetConfiguration = {
    ...DEFAULT_CHAT_CONFIGURATION,
    ...databaseSettings,
    ...pageSettings,
    // Don't allow user to override the following settings
    disabled: databaseSettings.disabled,
    watermark: databaseSettings.watermark ?? true,
    spacing: {
      ...DEFAULT_CHAT_CONFIGURATION.spacing,
      ...databaseSettings.spacing,
      ...pageSettings.spacing,
    },
    spacingMobile: {
      ...DEFAULT_CHAT_CONFIGURATION.spacingMobile,
      ...databaseSettings.spacingMobile,
      ...pageSettings.spacingMobile,
    },
    poweredByName: databaseSettings.poweredByName || "TOMIS",
    poweredByUrl:
      databaseSettings.poweredByUrl ||
      `https://tomis.tech/?utm_source=TOMIS+ChatBot&utm_medium=Powered+By+TOMIS&utm_campaign=${window.location.hostname}`,
  };

  getChatApi().chat.config = initConfig;

  return {
    success: true,
    config: initConfig,
    conversationHistory: conversation_history,
    handoffStatus: handoff_status,
    forms,
    requiredForm: required_form,
    trackingEnabled: tracking_enabled !== false,
  };
}

/** Default settings before customer makes changes to configuration.
 *
 * Settings excluded from this are not absolutely necessary for the chat widget to work
 */
export const DEFAULT_CHAT_CONFIGURATION: Pick<
  ChatWidgetConfiguration,
  | "name"
  | "image"
  | "color"
  | "description"
  | "position"
  | "spacing"
  | "spacingMobile"
  | "watermark"
  | "poweredByName"
  | "poweredByUrl"
> = {
  name: "TOMIS Assistant",
  image:
    "https://firebasestorage.googleapis.com/v0/b/tomis-bot.appspot.com/o/bot_icons%2Ftomis.png?alt=media&token=8abd2dc8-878d-42f8-b160-a5425f0204b5",
  color: "#309F87",
  description: "Welcome to my assistant",
  position: "right",
  spacing: {
    bottom: 40,
    side: 40,
  },
  spacingMobile: {
    bottom: 40,
    side: 40,
  },
  watermark: true,
  poweredByName: "TOMIS",
  poweredByUrl: `https://tomis.tech/?utm_source=TOMIS+ChatBot&utm_medium=Powered+By+TOMIS&utm_campaign=${window.location.hostname}`,
};

/** Ensure that Chat Widget Configuration only includes valid fields and types */
export function sanitizeSettings(
  settings: unknown,
): Partial<ChatWidgetConfiguration> {
  const ref = isObject(settings) ? (settings as Record<string, unknown>) : {};
  const {
    name,
    watermark,
    description,
    image,
    launcher,
    spacing,
    color,
    position,
    disabled,
    hide,
    size,
    spacingMobile,
    welcomeMessages,
    popupMessages,
    popupMessagesDelay,
    announcements,
    zIndex,
    poweredByName,
    poweredByUrl,
    widgetMode,
    initialFormKey,
  } = ref as Partial<ChatWidgetConfiguration>;

  return {
    ...(typeof name === "string" && { name }),
    ...(typeof color === "string" && { color }),
    ...(typeof image === "string" && image && { image }),
    ...(typeof launcher === "string" && { launcher }),
    ...(typeof watermark === "boolean" && { watermark }),
    ...(typeof description === "string" && { description }),
    ...(typeof position === "string" && { position }),
    ...(typeof disabled === "boolean" && { disabled }),
    ...(typeof hide === "boolean" && { hide }),
    ...(typeof size === "number" && { size }),
    ...(Array.isArray(welcomeMessages) && { welcomeMessages }),
    ...(Array.isArray(popupMessages) && { popupMessages }),
    ...(typeof popupMessagesDelay === "number" && { popupMessagesDelay }),
    ...(Array.isArray(announcements) && { announcements }),
    ...(typeof zIndex === "number" && { zIndex }),
    ...(typeof poweredByName === "string" && { poweredByName }),
    ...(typeof poweredByUrl === "string" && { poweredByUrl }),
    ...((widgetMode === "chat" ||
      widgetMode === "sms_form_then_chat" ||
      widgetMode === "sms_form_only") && { widgetMode }),
    ...(typeof initialFormKey === "string" &&
      initialFormKey && { initialFormKey }),
    ...(isObject(spacing) && {
      spacing: {
        side: (spacing as Spacing).side,
        bottom: (spacing as Spacing).bottom,
      },
    }),
    ...(isObject(spacingMobile) && {
      spacingMobile: {
        side: (spacingMobile as Spacing).side,
        bottom: (spacingMobile as Spacing).bottom,
      },
    }),
  };
}
